import {Card, styled, type CardProps, Stack} from '@mui/material';

import useGetPlanTreeNavigation from 'hooks/plan/useGetPlanTreeNavigation';
import useIsMounted from 'hooks/useIsMounted';
import useParseURL from 'hooks/useParseURL';

import {type HarvestRecommendationProcessed} from 'interfaces';
import {getSiteCageName} from 'helpers/extractData';

import HarvestCardContent from '../ShortTermPlanning/ActionCardContent';
import {type GrowthPerCage} from 'client';
import HarvestCardsTags from 'components/ShortTermPlanning/HarvestCardTags';
import ActionCardHeader from 'components/ShortTermPlanning/ActionCardHeader';

function HarvestRecommendationCard({
  harvest,
  growthByCage,
  isSelected,
  onSelection,
  hasMoreThanTwoSpecies,
}: {
  harvest: HarvestRecommendationProcessed;
  growthByCage?: GrowthPerCage;
  isSelected: boolean;
  onSelection: (recommendation: HarvestRecommendationProcessed) => void;
  hasMoreThanTwoSpecies: boolean;
}) {
  const {planId} = useParseURL();

  const isMounted = useIsMounted();
  const {planTreeNavigation} = useGetPlanTreeNavigation({planId, isMounted});

  const names = planTreeNavigation && getSiteCageName(harvest.cageId, planTreeNavigation);
  const siteId = harvest.siteId;

  return (
    <StyledCard
      elevation={1}
      onClick={() => {
        onSelection(harvest);
      }}
      isSelected={isSelected}
      data-testid="harvest-recommendation-card"
    >
      <Stack direction="column" spacing={1}>
        <ActionCardHeader
          site={names?.siteName ?? ''}
          cage={names?.cageName ?? ''}
          date={harvest.dateObject}
          species={hasMoreThanTwoSpecies ? harvest.species : undefined}
        />
        <HarvestCardContent action={harvest} isRecommendation isVisible={true} />
        <HarvestCardsTags {...{growthByCage, harvest, siteId, isRecommendation: true}} />
      </Stack>
    </StyledCard>
  );
}

export default HarvestRecommendationCard;

interface StyledCardProps extends CardProps {
  isSelected: boolean;
}

const StyledCard = styled(Card)<StyledCardProps>(({theme, isSelected}) => ({
  padding: theme.spacing(1),
  backgroundColor: isSelected ? theme.palette.background.default : theme.palette.background.paper,
}));
